import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { useApiMutation } from 'hooks/useApiMutation'
import { useSnackbar } from 'stores/useSnackbar'
import { useNavigate } from 'react-router-dom'
import { formatISO } from 'date-fns'
import { isNil } from 'lodash'
import { type CampaignSchema } from 'pages/Communications/CommunicationsNew/Message/CampaignSchema'
import { useCurrentStateStore } from 'stores/useCurrentStateStore'

const getCampaignId = (campaign: unknown): number | null => {
  if (campaign && typeof campaign === 'object') {
    const maybeCampaign = campaign as { id?: unknown }
    if (typeof maybeCampaign.id === 'number') {
      return maybeCampaign.id
    }
  }

  return null
}

export const usePostCampaign = () => {
  const post = useTypedApiClient({
    path: '/admin/campaigns',
    method: 'post',
  })
  const setMessage = useSnackbar((state) => state.setMessage)
  const navigate = useNavigate()
  const { currentStateId } = useCurrentStateStore()

  return useApiMutation<typeof post, typeof CampaignSchema>({
    mutationFn: async (formData) => {
      const {
        typeOfMessage,
        importPatrons,
        scheduledDate,
        licensedEstablishments,
        contestId,
        excludeType,
        excludedId,
        subjectLine,
        messageContent,
        corporateAccountId,
        tierId,
        linkLabel,
        linkUrl,
        filterGender,
        filterAgeRange,
        filterState,
        filterZipCode,
        filterWinnersOnly,
        filterIncompleteProfiles,
        filterAccountCreatedDate,
        filterNotCheckedInSinceDate,
      } = formData
      if (isNil(scheduledDate)) {
        throw new Error('Scheduled date is required')
      }
      const { data } = await post({
        importPatrons,
        licensedEstablishments:
          typeOfMessage === 'Location' ? licensedEstablishments : undefined,
        contestId: typeOfMessage === 'Sweepstakes' ? contestId : undefined,
        corporateAccountId:
          typeOfMessage === 'Corporate' ? corporateAccountId : undefined,
        excludedCorporateAccountId:
          typeOfMessage === 'AllPatrons' && excludeType === 'CA'
            ? excludedId
            : undefined,
        excludedOrganizationId:
          typeOfMessage === 'AllPatrons' && excludeType === 'Org'
            ? excludedId
            : undefined,
        excludedLicensedEstablishmentId:
          typeOfMessage === 'AllPatrons' && excludeType === 'LE'
            ? excludedId
            : undefined,
        excludePatronsWithCheckins: !!(
          typeOfMessage === 'AllPatrons' &&
          excludeType === 'PatronsWithCheckins'
        ),
        tierId: typeOfMessage === 'AllPatronsByTier' ? tierId : undefined,
        subject: subjectLine,
        body: messageContent,
        deliveryDate: formatISO(scheduledDate),
        type: typeOfMessage,
        linkLabel,
        linkUrl,
        filterGender,
        filterAgeRange,
        filterState,
        filterZipCode,
        filterWinnersOnly,
        filterIncompleteProfiles,
        filterAccountCreatedDate: isNil(filterAccountCreatedDate)
          ? undefined
          : formatISO(filterAccountCreatedDate),
        filterNotCheckedInSinceDate: isNil(filterNotCheckedInSinceDate)
          ? undefined
          : formatISO(filterNotCheckedInSinceDate),
        currentStateId: currentStateId ?? 1,
      })
      return data
    },
    onError: (error) => {
      alert(error.data.message)
    },
    onSuccess: (campaign, variables) => {
      setMessage('Message created successfully', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })

      const campaignId = getCampaignId(campaign)

      if (variables.importPatrons && campaignId) {
        navigate(`/Communications/Messages/${campaignId}`)
        return
      }

      navigate('/Communications/Messages')
    },
  })
}
